import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Toggle from 'react-bootstrap-toggle';
import { formatters } from 'common';
import classNames from 'classnames';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { createStructuredSelector } from 'reselect';
import { isNil, isEmpty } from 'lodash';
import { selectResult, updateWireRequest } from 'services/updateWire';
import styles from './styles.css';
import messages from './messages';
import DefListColumn from '../DefListColumn';
import { messages as transferPanelMessages } from '../TransferPanel/messages';
import { selectUpdateWireFailure } from './selectors';


export const TransferPanelIncomingWireDetails = ({
  updateWireDetails,
  transferId,
  dateRequested,
  amount,
  fedWireReferenceNumber,
  externalId,
  houseBankAccountNumber,
  wireIsDomestic,
  wireIsThirdParty,
  ShouldUpdateWireDetails,
  wireIsReturn,
  transferAccountValue,
  wireUpdateFailure,
}) => {
  const [isThirdParty, setIsThirdParty] = useState(wireIsThirdParty);
  const [isForeign, setIsForeign] = useState(!isNil(wireIsDomestic) ? !wireIsDomestic : null);

  const updateWire = (payload) => {
    updateWireDetails(transferId, payload);
  };

  const handleThirdPartyChange = (state) => {
    updateWire({
      isThirdParty: state,
      isDomestic: !isForeign,
    });
    if (!isEmpty(transferAccountValue)) {
      setIsThirdParty(!isThirdParty); // To allow toggle animation
    }
  };

  const handleIsForeignChange = (state) => {
    updateWire({
      isDomestic: !state,
      isThirdParty,
    });
    setIsForeign(!isForeign); // To allow toggle animation
  };

  const convertBooleanToText = (bool) => {
    if (isNil(bool)) {
      return 'N/A';
    }
    return bool ? 'YES' : 'NO';
  };

  const checkTogglesAreCorrect = (toggleState, paramState, setToggle) => {
    if (toggleState && isNil(paramState)) {
      setToggle(paramState);
    }
  };

  useEffect(() => {
    checkTogglesAreCorrect(isThirdParty, wireIsThirdParty, setIsThirdParty);
    checkTogglesAreCorrect(isForeign, wireIsDomestic, setIsForeign);
  }, [isThirdParty, wireUpdateFailure, transferAccountValue]);

  const thirdPartyToggle = (
    <div data-test-handle="wireDetails__isThirdPartyToggle">
      <Toggle
        onClick={handleThirdPartyChange}
        on=""
        off=""
        size="sm"
        // disabled={isEmpty(transferAccountValue)}
        onClassName="btn-apex-toggle-on"
        offClassName="btn-apex-toggle-off"
        handleClassName="btn-apex-toggle-handle"
        active={isThirdParty}
        style={{
          borderRadius: '13px',
          marginRight: '3px',
          lineHeight: '1',
        }}
        width="40px"
        height="17px"
      />
      <span data-test-handle="wireDetails__isThirdPartyToggleText">{isNil(isThirdParty) ? 'N/A' : convertBooleanToText(isThirdParty)}</span>
    </div>
  );

  const isForeignToggle = (
    <div data-test-handle="wireDetails__isForeignToggle">
      <Toggle
        onClick={handleIsForeignChange}
        on=""
        off=""
        size="sm"
        onClassName="btn-apex-toggle-on"
        offClassName="btn-apex-toggle-off"
        handleClassName="btn-apex-toggle-handle"
        active={isForeign}
        style={{
          borderRadius: '13px',
          marginRight: '3px',
          lineHeight: '1',
        }}
        width="40px"
        height="17px"
      />
      <span data-test-handle="wireDetails__isForeignToggleText">{isNil(isForeign) ? 'N/A' : convertBooleanToText(isForeign)}</span>
    </div>
  );

  return (
    <React.Fragment>
      <div className={styles.leftPane}>
        <DefListColumn
          className={styles.defList}
          title={<FormattedMessage {...transferPanelMessages.transferId} />}
          data={transferId}
          dataTestHandle="wireDetails__transferId"
        />
        <DefListColumn
          className={styles.defList}
          title={<FormattedMessage {...messages.dateCreated} />}
          data={dateRequested}
          dataTestHandle="wireDetails__dateRequested"
        />
        <DefListColumn
          className={styles.defList}
          title={<FormattedMessage {...transferPanelMessages.amount} />}
          data={formatters.formatDollars(amount)}
          dataTestHandle="wireDetails__amount"
        />
        {wireIsReturn &&
          <DefListColumn
            className={classNames(styles.defList, styles.isReturnBool)}
            title={<FormattedMessage {...messages.wireIsReturn} />}
            data="TRUE"
            dataTestHandle="wireDetails__wireIsReturn"
          />}
      </div>
      <div className={styles.rightPane}>
        <DefListColumn
          className={styles.defList}
          title={<FormattedMessage {...messages.isThirdParty} />}
          data={
            ShouldUpdateWireDetails ? thirdPartyToggle : convertBooleanToText(isThirdParty)
          }
          dataTestHandle="wireDetails__isThirdParty"
        />
        <DefListColumn
          className={styles.defList}
          title={<FormattedMessage {...messages.isForeign} />}
          data={
            ShouldUpdateWireDetails ? isForeignToggle : convertBooleanToText(isForeign)
          }
          dataTestHandle="wireDetails__isForeign"
        />
        <DefListColumn
          className={styles.defList}
          title={<FormattedMessage {...messages.fedRefNum} />}
          data={fedWireReferenceNumber}
          dataTestHandle="wireDetails__fedRefNum"
        />
        <DefListColumn
          className={styles.defList}
          title={<FormattedMessage {...messages.externalId} />}
          data={externalId}
          dataTestHandle="wireDetails__externalId"
        />
        <DefListColumn
          className={styles.defList}
          title={<FormattedMessage {...messages.houseBankAccountNumber} />}
          data={houseBankAccountNumber}
          dataTestHandle="wireDetails__houseBankAccountNumber"
        />
      </div>
    </React.Fragment>
  );
};

TransferPanelIncomingWireDetails.propTypes = {
  updateWireDetails: PropTypes.func.isRequired,
  wireUpdateFailure: PropTypes.bool,
  wireUpdateResult: PropTypes.object,
  transferId: PropTypes.string.isRequired,
  transferState: PropTypes.string,
  dateRequested: PropTypes.string,
  amount: PropTypes.number,
  fedWireReferenceNumber: PropTypes.string,
  externalId: PropTypes.string,
  houseBankAccountNumber: PropTypes.string,
  wireIsDomestic: PropTypes.bool,
  wireIsThirdParty: PropTypes.bool,
  wireIsReturn: PropTypes.bool,
  ShouldUpdateWireDetails: PropTypes.bool.isRequired,
  transferAccountValue: PropTypes.string,
};

TransferPanelIncomingWireDetails.defaultProps = {
  wireUpdateFailure: null,
  wireUpdateResult: null,
  transferState: null,
  dateRequested: '',
  amount: null,
  fedWireReferenceNumber: '',
  externalId: '',
  houseBankAccountNumber: '',
  wireIsDomestic: null,
  wireIsThirdParty: null,
  wireIsReturn: false,
  transferAccountValue: null,
};

/* istanbul ignore next */
const mapStateToProps = (/*state*/) => createStructuredSelector({
  wireUpdateFailure: selectUpdateWireFailure,
  wireUpdateResult: selectResult,
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) => bindActionCreators({
  updateWireDetails: updateWireRequest,
}, dispatch);


export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(TransferPanelIncomingWireDetails));



// WEBPACK FOOTER //
// ./src/components/TransferPanelWireDetails/TransferPanelIncomingWireDetails.js